import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Copy } from "lucide-react";
import { Button, EmptyState, Icon, Page, PlatformIcon, Spinner } from "@cntrl/ui";
import { RequireBand } from "../../app/RequireBand.js";
import { useCapability } from "../../app/bandContext.js";
import { useReleases, type ReleaseSummary } from "../../app/hooks/useReleases.js";
import { useRelease, useSmartLinkStats } from "../../app/hooks/useRelease.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { PUBLIC_WEB_URL } from "../../config/env.js";
import { TodoCard } from "./home/homeCards.js";
import { TaskCreateModal } from "../tasks/TaskCreateModal.js";
import { ReleaseArtwork } from "../releases/ReleaseArtwork.js";
import { DSPS, dspDef } from "../releases/dsp.js";
import {
  catalogOrder,
  countdownBlock,
  displayStatus,
  formatDollars,
  formatMonthDay,
  formatShortDate,
  lifetimeClicks,
  lifetimeCtrPct,
  releaseStatusLabel,
  releaseTypeLabel,
} from "../releases/releaseFormat.js";
import styles from "./MobileReleasesScreen.module.scss";

/**
 * `/releases` on mobile — the ART-189 redesign. The desktop hub ({@link ReleasesScreen}) leads with a
 * featured hero; on a phone Ryan's design drops it and opens straight into a swipeable CATALOG strip. Tapping
 * a card SELECTS it (a crimson outline) and everything below — the release card, the four-up glance, the open
 * to-dos and the smart-link card — reflects that one release IN PLACE, so the screen is the catalogue and the
 * per-release read at once. It is READ-ONLY (spec §3: mobile books/edits nothing), so there is no create
 * affordance and no navigation away — the shell header carries the "Releases" title and the band switcher
 * names the band.
 *
 * Below `lg` the router renders THIS; at/above it, the desktop hub. The two never mount together.
 */
export function MobileReleasesScreen({ initialReleaseId }: {
  /** `?release=` — open on this release (where a phone lands from any link to a single release). */
  initialReleaseId?: string;
} = {}) {
  return (
    <RequireBand>
      <Inner initialReleaseId={initialReleaseId} />
    </RequireBand>
  );
}

function Inner({ initialReleaseId }: { initialReleaseId?: string }) {
  const canRead = useCapability("releases:read");
  const all = useReleases();
  const releases = useMemo(() => all.data?.items ?? [], [all.data]);
  const ordered = useMemo(() => catalogOrder(releases), [releases]);

  // The selected release is tracked by id (a released/deleted release then falls back to the first card rather
  // than pinning a stale selection). Default: the first in catalogue order — the soonest in cycle.
  const [selectedId, setSelectedId] = useState<string | null>(initialReleaseId ?? null);
  const selected =
    ordered.find((r) => r.releaseId === selectedId) ?? ordered[0] ?? undefined;

  // Opened on a release (`?release=`): bring its card into view in the strip once, horizontally only — it may sit
  // past the edge, and the selection outline is the only thing saying which release the page below is about.
  const stripRef = useRef<HTMLUListElement>(null);
  const revealed = useRef(false);
  useEffect(() => {
    const strip = stripRef.current;
    if (!initialReleaseId || revealed.current || !strip) return;
    const card = strip.querySelector<HTMLElement>("[data-selected]");
    if (!card) return;
    revealed.current = true;
    const pad = parseFloat(getComputedStyle(strip).paddingLeft) || 0;
    strip.scrollLeft += card.getBoundingClientRect().left - strip.getBoundingClientRect().left - pad;
  }, [initialReleaseId, ordered]);

  if (!canRead) {
    return (
      <Page title="Releases" as="section">
        <EmptyState
          title="Releases aren't part of your access"
          description="Ask an owner or manager if you need the Releases workspace."
        />
      </Page>
    );
  }

  if (all.isLoading) {
    return (
      <Page title="Releases" as="section">
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={22} label="Loading releases" />
        </div>
      </Page>
    );
  }
  if (all.isError && all.data == null) {
    return (
      <Page title="Releases" as="section">
        <EmptyState
          title="We couldn't load your releases"
          description={all.error instanceof ApiError ? all.error.message : "Please try again."}
          action={<Button onClick={() => void all.refetch()}>Retry</Button>}
        />
      </Page>
    );
  }
  if (releases.length === 0) {
    return (
      <Page title="Releases" as="section">
        <EmptyState
          title="No releases yet"
          description="Your catalogue appears here. Creating a release happens on desktop."
        />
      </Page>
    );
  }

  return (
    <Page title="Releases" as="section">
      {/* The catalogue strip is the first thing on the page — no eyebrow, no summary line (Ryan, ART-189). */}
      <ul ref={stripRef} className={styles.strip} aria-label="Release catalog">
        {ordered.map((r) => (
          <li key={r.releaseId} className={styles.stripItem}>
            <CatalogCard
              release={r}
              selected={r.releaseId === selected?.releaseId}
              onSelect={() => setSelectedId(r.releaseId ?? null)}
            />
          </li>
        ))}
      </ul>

      {selected && <ReleaseDetail key={selected.releaseId} release={selected} />}
    </Page>
  );
}

// ---- catalog strip -------------------------------------------------------------------------------------

function CatalogCard({
  release,
  selected,
  onSelect,
}: {
  release: ReleaseSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={styles.catCard}
      data-selected={selected || undefined}
      aria-pressed={selected}
      onClick={onSelect}
    >
      <ReleaseArtwork
        seed={release.releaseId}
        mediaId={release.coverMediaId}
        imageUrl={release.coverImageUrl}
        size="tile"
        className={styles.catArt}
      />
      <span className={styles.catTitle}>{release.title || "Untitled release"}</span>
      <span className={styles.catDate}>{formatMonthDay(release.releaseDate) || "—"}</span>
    </button>
  );
}

// ---- selected release ----------------------------------------------------------------------------------

function ReleaseDetail({ release }: { release: ReleaseSummary }) {
  const q = useRelease(release.releaseId);
  const stats = useSmartLinkStats(release.releaseId);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const toDo = q.data?.data?.toDo;
  const assets = q.data?.data?.assets?.progress;
  const budget = q.data?.data?.budget;
  const dspLinks = q.data?.data?.dspLinks ?? undefined;
  const smartLink = q.data?.data?.smartLink;

  const count = countdownBlock(release.releaseDate);
  const bucket = displayStatus(release);

  return (
    <>
      {/* The release card — the reference's [type] / [name] / [date] with the artwork and the count block, all
          centred to the card (Ryan: the prior layout "feels off"). The redundant second "Single · May 8" line
          is gone; type appears once in the eyebrow, the date once below the title. */}
      <div className={styles.releaseCard}>
        <ReleaseArtwork
          seed={release.releaseId}
          mediaId={release.coverMediaId}
          imageUrl={release.coverImageUrl}
          size="tile"
          className={styles.releaseArt}
          lazy={false}
        />
        <div className={styles.releaseText}>
          <span className={styles.releaseType}>
            {releaseStatusLabel(bucket)} · {releaseTypeLabel(release.releaseType)}
          </span>
          <span className={styles.releaseName}>{release.title || "Untitled release"}</span>
          <span className={styles.releaseDate}>{formatShortDate(release.releaseDate) || "No date"}</span>
        </div>
        <div className={styles.countdown}>
          {count.value !== null && <span className={styles.countNum}>{count.value}</span>}
          <span className={styles.countLabel}>{count.label}</span>
        </div>
      </div>

      {/* The four-up glance — Assets · To-do · Smart-link visits (30d) · Ad spend. Each dashes when its read is
          unavailable (the leak contract), never a fabricated 0. */}
      <div className={styles.kpis}>
        <Kpi label="Assets" value={assets ? `${assets.added ?? 0}/${assets.total ?? 0}` : "—"} />
        <Kpi label="To-do" value={toDo ? `${toDo.doneCount ?? 0}/${toDo.total ?? 0}` : "—"} />
        <Kpi
          label="Visits · 30d"
          value={stats.isError || stats.data?.visits30d == null ? "—" : String(stats.data.visits30d)}
        />
        <Kpi
          label="Ad spend"
          value={budget?.adSpend30dCents == null ? "—" : formatDollars(budget.adSpend30dCents)}
        />
      </div>

      {/* The dashboard's mobile To-do card, scoped to this release (Ryan, ART-189) — same rows, faces and
          overdue treatment, filtered, with a designed empty message and a dashed "+ Add task" footer (this
          screen has no page-level create button). The new task is scoped to this release. */}
      <TodoCard
        releaseId={release.releaseId as string}
        emptyText="No items on list"
        onAddTask={() => setCreateTaskOpen(true)}
      />

      {createTaskOpen && (
        <TaskCreateModal
          releaseId={release.releaseId as string}
          onClose={() => setCreateTaskOpen(false)}
          onCreated={() => setCreateTaskOpen(false)}
        />
      )}

      <SmartLinkCard
        release={release}
        bandSlug={smartLink?.bandSlug ?? undefined}
        stats={stats.data}
        statsError={stats.isError}
        dspLinks={dspLinks}
      />
    </>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.kpi}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={styles.kpiVal}>{value}</span>
    </div>
  );
}

// ---- smart link ----------------------------------------------------------------------------------------

function SmartLinkCard({
  release,
  bandSlug,
  stats,
  statsError,
  dspLinks,
}: {
  release: ReleaseSummary;
  bandSlug: string | undefined;
  stats: ReturnType<typeof useSmartLinkStats>["data"];
  statsError: boolean;
  dspLinks: { dsp?: string | null; url?: string | null }[] | undefined;
}) {
  const [copied, setCopied] = useState(false);
  const url = publicSmartLinkUrl(bandSlug, release.smartLinkSlug);

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  const clicks = lifetimeClicks(stats, statsError);
  const ctr = lifetimeCtrPct(clicks, stats?.visitsLifetime);

  return (
    // A SOLID card, so its label sits INSIDE it (Ryan's rule: solid card ⇒ label in; loose entities ⇒ label
    // out — which is why the catalogue strip has none).
    <section className={styles.smartCard} aria-label="Smart link">
      {/* The URL itself is gone (Ryan, ART-189): the eyebrow sits on the left and the button carries the whole
          affordance — "Copy link" — on the right. */}
      <div className={styles.smartHeadRow}>
        <h2 className={styles.smartHead}>Smart link</h2>
        <Button variant="ghost" size="sm" onClick={() => void copy()} disabled={!url}>
          <Icon icon={Copy} size={16} /> {copied ? "Copied" : "Copy link"}
        </Button>
      </div>

        <div className={styles.smartStats}>
          <SmartStat
            label="Visits · life"
            value={statsError || stats?.visitsLifetime == null ? "—" : String(stats.visitsLifetime)}
          />
          <SmartStat label="Clicks · life" value={clicks == null ? "—" : String(clicks)} />
          <SmartStat label="CTR" value={ctr == null ? "—" : `${ctr}%`} />
        </div>

      <DspBars stats={stats} statsError={statsError} dspLinks={dspLinks} />
    </section>
  );
}

function SmartStat({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.smartStat}>
      <span className={styles.smartVal}>{value}</span>
      <span className={styles.smartLabel}>{label}</span>
    </div>
  );
}

/**
 * The per-DSP lifetime click bars, with the platform's actual brand mark beside each (Ryan: "put the actual
 * SVG beside them", replacing the letter badges). Row set + ordering mirror the desktop `DspClicksChart`: the
 * union of the release's configured buttons and any DSP with recorded clicks, in the fixed `DSPS` order. The
 * bar width is share of the MAX row; the right column is the raw count over its share of the total.
 */
function DspBars({
  stats,
  statsError,
  dspLinks,
}: {
  stats: ReturnType<typeof useSmartLinkStats>["data"];
  statsError: boolean;
  dspLinks: { dsp?: string | null; url?: string | null }[] | undefined;
}) {
  const unavailable = statsError || !stats;
  const byDsp = new Map<string, number>(
    (stats?.lifetimeClicksByDsp ?? []).map((c) => [c.dsp ?? "", c.clicks ?? 0] as [string, number]),
  );
  byDsp.delete("");

  const linkKeys = (dspLinks ?? []).filter((l) => l.url && l.dsp).map((l) => l.dsp as string);
  const keys = new Set<string>([...linkKeys, ...byDsp.keys()]);
  const rows = DSPS.filter((d) => keys.has(d.key)).map((d) => ({ def: d, clicks: byDsp.get(d.key) ?? 0 }));
  for (const key of keys)
    if (!DSPS.some((d) => d.key === key)) rows.push({ def: dspDef(key), clicks: byDsp.get(key) ?? 0 });

  const max = rows.reduce((m, r) => Math.max(m, r.clicks), 0);
  const total = rows.reduce((sum, r) => sum + r.clicks, 0);

  if (rows.length === 0) {
    // A loaded-but-empty breakdown shows nothing (no add-links / no-clicks copy on mobile); only an
    // UNAVAILABLE read says so, so a dashed set of tiles above is not left unexplained.
    if (!unavailable) return null;
    return <p className={styles.empty}>Per-DSP clicks aren't available right now.</p>;
  }

  return (
    <ul className={styles.barList}>
      {rows.map(({ def, clicks }) => {
        const share = !unavailable && total > 0 ? Math.round((clicks / total) * 100) : null;
        return (
          <li key={def.key} className={styles.barRow}>
            <span className={styles.dspBadge} style={{ ["--dsp-brand"]: `var(${def.colorVar})` } as CSSProperties}>
              <PlatformIcon platform={def.key} size={16} />
            </span>
            <span className={styles.barBody}>
              <span className={styles.barName}>{def.label}</span>
              <span className={styles.barTrack}>
                <span
                  className={styles.barFill}
                  style={
                    {
                      width: `${!unavailable && max > 0 ? (clicks / max) * 100 : 0}%`,
                      background: `var(${def.colorVar})`,
                    } as CSSProperties
                  }
                />
              </span>
            </span>
            <span className={styles.barNums}>
              <span className={styles.barCount}>{unavailable ? "—" : clicks}</span>
              {share !== null && <span className={styles.barShare}>{share}%</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ---- helpers -------------------------------------------------------------------------------------------

/** The absolute public smart-link URL, or undefined until the band slug + song slug both resolve. */
function publicSmartLinkUrl(bandSlug: string | undefined, songSlug: string | null | undefined): string | undefined {
  if (!bandSlug || !songSlug) return undefined;
  const base = PUBLIC_WEB_URL || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/${bandSlug}/${songSlug}`;
}
